import { useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from './context/AuthContext';
import { useProfile } from './context/ProfileContext';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { List } from '@phosphor-icons/react';
import './index.css';

import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Forgot2FAPage from './pages/Forgot2FAPage';
import Reset2FAPage from './pages/Reset2FAPage';
import DashboardPage from './pages/DashboardPage';
import FacilitiesPage from './pages/FacilitiesPage';
import FacilityDetailPage from './pages/FacilityDetailPage';
import ContactsPage from './pages/ContactsPage';
import ScanJobsPage from './pages/ScanJobsPage';
import LeadScoresPage from './pages/LeadScoresPage';
import DuplicatesPage from './pages/DuplicatesPage';
import ExportsPage from './pages/ExportsPage';
import CompliancePage from './pages/CompliancePage';
import EnrichmentPage from './pages/EnrichmentPage';
import EmailCampaignsPage from './pages/EmailCampaignsPage';
import ImportExportPage from './pages/ImportExportPage';
import QuotesOrdersPage from './pages/QuotesOrdersPage';
import ConversationsPage from './pages/ConversationsPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import LearningInsightsPage from './pages/LearningInsightsPage';
import SearchTermsPage from './pages/SearchTermsPage';
import SearchTermsV2Page from './pages/SearchTermsV2Page';
import InboxPage from './pages/InboxPage';
import GlobalInboxPage from './pages/GlobalInboxPage';
import MailboxesPage from './pages/MailboxesPage';
import AIGuidelinesPage from './pages/AIGuidelinesPage';
import SecuritySettingsPage from './pages/SecuritySettingsPage';
import ReplyLearningPage from './pages/ReplyLearningPage';
import ArticleMappingsPage from './pages/ArticleMappingsPage';
import ProfilesPage from './pages/ProfilesPage';
import BackupsPage from './pages/BackupsPage';
import ProfileMovePage from './pages/ProfileMovePage';
import Sidebar from './components/Sidebar';
import { AuthProvider } from './context/AuthContext';
import { ProfileProvider } from './context/ProfileContext';

const FALLBACK_LOGO = "https://customer-assets.emergentagent.com/job_aquatic-crm-sync/artifacts/d0yhpcoq_PoolFIX%20-%20Logo%20-%20RGB%20350px.jpg";
const API = process.env.REACT_APP_BACKEND_URL;

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const { activeProfile } = useProfile();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  const mobileLogo = activeProfile?.logo_url
    ? (activeProfile.logo_url.startsWith('http') ? activeProfile.logo_url : `${API}${activeProfile.logo_url}`)
    : FALLBACK_LOGO;
  return (
    <div className="flex min-h-screen bg-[#FAFAFA]" data-testid="app-layout">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setDrawerOpen(false)}
          data-testid="mobile-drawer-backdrop"
        />
      )}
      <div
        className={`md:hidden fixed top-0 left-0 h-full z-50 transform transition-transform duration-200 ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="mobile-drawer"
      >
        <Sidebar onNavigate={() => setDrawerOpen(false)} />
      </div>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden flex items-center justify-between gap-3 px-4 py-3 border-b border-zinc-200 bg-white sticky top-0 z-30">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Menu openen"
            className="p-2 -ml-2 rounded-sm hover:bg-zinc-100 active:bg-zinc-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <List size={22} />
          </button>
          <img src={mobileLogo} alt={activeProfile?.name || 'Logo'} className="h-7 w-auto max-w-[150px] object-contain" />
          <span className="w-[44px]" />
        </header>

        <main className="flex-1 p-3 sm:p-5 md:p-6 overflow-x-hidden overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/forgot-2fa" element={<Forgot2FAPage />} />
      <Route path="/reset-2fa" element={<Reset2FAPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/facilities" element={<ProtectedRoute><FacilitiesPage /></ProtectedRoute>} />
      <Route path="/facilities/:id" element={<ProtectedRoute><FacilityDetailPage /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
      <Route path="/scans" element={<ProtectedRoute><ScanJobsPage /></ProtectedRoute>} />
      <Route path="/lead-scores" element={<ProtectedRoute><LeadScoresPage /></ProtectedRoute>} />
      <Route path="/enrichment" element={<ProtectedRoute><EnrichmentPage /></ProtectedRoute>} />
      <Route path="/duplicates" element={<ProtectedRoute><DuplicatesPage /></ProtectedRoute>} />
      <Route path="/exports" element={<ProtectedRoute><ExportsPage /></ProtectedRoute>} />
      <Route path="/email" element={<ProtectedRoute><EmailCampaignsPage /></ProtectedRoute>} />
      <Route path="/import-export" element={<ProtectedRoute><ImportExportPage /></ProtectedRoute>} />
      <Route path="/quotes-orders" element={<ProtectedRoute><QuotesOrdersPage /></ProtectedRoute>} />
      <Route path="/conversations" element={<ProtectedRoute><ConversationsPage /></ProtectedRoute>} />
      <Route path="/knowledge-base" element={<ProtectedRoute><KnowledgeBasePage /></ProtectedRoute>} />
      <Route path="/learning" element={<ProtectedRoute><LearningInsightsPage /></ProtectedRoute>} />
      <Route path="/search-terms" element={<ProtectedRoute><SearchTermsV2Page /></ProtectedRoute>} />
      <Route path="/search-terms-legacy" element={<ProtectedRoute><SearchTermsPage /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
      <Route path="/global-inbox" element={<ProtectedRoute><GlobalInboxPage /></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><CompliancePage /></ProtectedRoute>} />
      <Route path="/settings/mailboxes" element={<ProtectedRoute><MailboxesPage /></ProtectedRoute>} />
      <Route path="/settings/ai-guidelines" element={<ProtectedRoute><AIGuidelinesPage /></ProtectedRoute>} />
      <Route path="/settings/security" element={<ProtectedRoute><SecuritySettingsPage /></ProtectedRoute>} />
      <Route path="/settings/reply-learning" element={<ProtectedRoute><ReplyLearningPage /></ProtectedRoute>} />
      <Route path="/settings/article-mappings" element={<ProtectedRoute><ArticleMappingsPage /></ProtectedRoute>} />
      <Route path="/settings/profiles" element={<ProtectedRoute><ProfilesPage /></ProtectedRoute>} />
      <Route path="/settings/backups" element={<ProtectedRoute><BackupsPage /></ProtectedRoute>} />
      <Route path="/settings/profile-move" element={<ProtectedRoute><ProfileMovePage /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProfileProvider>
          <AppRouter />
        </ProfileProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
